/**
 * Whose the add-in's state on this computer is. On a shared computer the next
 * person to sign in must not find the last one's place in the pane: the tab and
 * client project left open, the document reviews kept for each document. The
 * state belongs to the user who signed in last; a different user signing in
 * clears it and starts the pane from nothing (TaskPane `onRestart`).
 */
import { getEnvironment } from './tokenStorage';
import { REVIEW_COMPLETE_STATE_STORAGE_KEY, REVIEW_RESULTS_STORAGE_KEY } from './reviewStateStorageKeys';
import { SESSION_NAV_KEY } from './sessionNavigationStorage';

const OWNER_KEY = `courtyard_word_state_owner_${getEnvironment()}`;

/** What a user leaves on this computer for the next time they open the pane. */
const USER_STATE_KEYS = [SESSION_NAV_KEY, REVIEW_RESULTS_STORAGE_KEY, REVIEW_COMPLETE_STATE_STORAGE_KEY];

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

/** The user a sign-in token belongs to: its Firebase user id, else its email; null when it cannot be read. */
export function userOfToken(token: string | null | undefined): string | null {
  const part = (token || '').split('.')[1];
  if (!part) return null;
  try {
    const padded = part + '='.repeat((4 - (part.length % 4)) % 4);
    const payload = JSON.parse(atob(padded.replace(/-/g, '+').replace(/_/g, '/'))) as Record<string, unknown>;
    const user = payload.user_id || payload.sub || payload.email;
    return typeof user === 'string' && user ? user : null;
  } catch {
    return null;
  }
}

/** The user the state on this computer belongs to; null when none was recorded. */
export function stateOwner(): string | null {
  try {
    return storage()?.getItem(OWNER_KEY) || null;
  } catch {
    return null;
  }
}

export function setStateOwner(user: string): void {
  try {
    storage()?.setItem(OWNER_KEY, user);
  } catch {
    // Quota / private mode: the state is then never handed to anyone else either, since it cannot be kept.
  }
}

/**
 * Makes the state on this computer `user`'s; returns true when another user's state had to be removed
 * first. State with no recorded owner is kept for `user` when `adoptUnowned` is set: a pane that opens
 * already signed in shows today what the signed-in user left. At a sign-in it may be anyone's.
 */
export function claimUserState(user: string, { adoptUnowned }: { adoptUnowned: boolean }): boolean {
  const owner = stateOwner();
  if (owner === user) return false;
  if (owner === null && adoptUnowned) {
    setStateOwner(user);
    return false;
  }
  clearUserState();
  setStateOwner(user);
  return true;
}

/** Removes what the previous user left on this computer. */
export function clearUserState(): void {
  const store = storage();
  if (!store) return;
  for (const key of USER_STATE_KEYS) {
    try {
      store.removeItem(key);
    } catch {
      // Continue to the next key.
    }
  }
}
